import React, { useState, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { Calendar, Clock, CalendarDays } from 'lucide-react';
import Header from '../../../components/layout/Header';
import Footer from '../../../components/layout/Footer';
import Breadcrumbs from '../../../components/ui/Breadcrumbs';
import ToolSidebar from '../../../components/layout/ToolSidebar';
import { useTheme } from '../../../contexts/ThemeContext';
import { getToolById } from '../../../data/tools';

// Импорт компонентов калькулятора
import CalculationForm from './TimeIntervalCalculatorPage/components/CalculationForm';
import ResultsDisplay from './TimeIntervalCalculatorPage/components/ResultsDisplay';
import QuickDatesSidebar from './TimeIntervalCalculatorPage/components/QuickDatesSidebar';
import SavedCalculationsSidebar from './TimeIntervalCalculatorPage/components/SavedCalculationsSidebar';
import TipsSidebar from './TimeIntervalCalculatorPage/components/TipsSidebar';
import InfoContent from './TimeIntervalCalculatorPage/components/InfoContent';
import WorkingDaysCalculator from './TimeIntervalCalculatorPage/components/WorkingDaysCalculator';
import DateAddSubtract from './TimeIntervalCalculatorPage/components/DateAddSubtract';
import AgeCalculator from './TimeIntervalCalculatorPage/components/AgeCalculator';

// Импорт утилитарных функций
import { calculateDateDifference, getWorkingDaysBetween } from '../../../utils/calculations/date';

// Определение начального состояния формы
export const initialFormData = {
  startDate: new Date().toISOString().split('T')[0],
  endDate: new Date().toISOString().split('T')[0],
  includeEnd: true,
  calculationType: 'standard', // 'standard', 'working', 'add-subtract', 'age'
  
  // Параметры для рабочих дней
  includeHolidays: true,
  regionCode: 'RU-MOW', // Москва по умолчанию
  
  // Параметры для добавления/вычитания дат
  operation: 'add', // 'add' или 'subtract'
  datePeriod: 7, // количество дней, месяцев, лет
  periodType: 'days', // 'days', 'weeks', 'months', 'years'
  onlyWorkingDays: false,
  
  // Параметры для расчета возраста
  birthdayDate: new Date(new Date().setFullYear(new Date().getFullYear() - 30)).toISOString().split('T')[0],
  referenceDate: new Date().toISOString().split('T')[0],
};

// Определение российских регионов
export const russianRegions = [
  { code: 'RU-MOW', name: 'Москва' },
  { code: 'RU-SPE', name: 'Санкт-Петербург' },
  { code: 'RU-KDA', name: 'Краснодарский край' },
  { code: 'RU-SVE', name: 'Свердловская область' },
  { code: 'RU-NVS', name: 'Новосибирская область' },
  { code: 'RU-KYA', name: 'Красноярский край' },
  { code: 'RU-SAM', name: 'Самарская область' },
  { code: 'RU-CHE', name: 'Челябинская область' },
  { code: 'RU-NIZ', name: 'Нижегородская область' },
  { code: 'RU-ROS', name: 'Ростовская область' },
  { code: 'RU-TAT', name: 'Республика Татарстан' },
  { code: 'RU-BA', name: 'Республика Башкортостан' },
  { code: 'RU-PER', name: 'Пермский край' },
  { code: 'RU-OMK', name: 'Омская область' },
  { code: 'RU-VGG', name: 'Волгоградская область' },
  { code: 'RU', name: 'Общероссийский календарь' },
];

// Список российских праздников
export const russianHolidays = [
  { date: '2025-01-01', name: 'Новый Год', type: 'federal' },
  { date: '2025-01-02', name: 'Новогодние каникулы', type: 'federal' },
  { date: '2025-01-03', name: 'Новогодние каникулы', type: 'federal' },
  { date: '2025-01-04', name: 'Новогодние каникулы', type: 'federal' },
  { date: '2025-01-05', name: 'Новогодние каникулы', type: 'federal' },
  { date: '2025-01-06', name: 'Новогодние каникулы', type: 'federal' },
  { date: '2025-01-07', name: 'Рождество Христово', type: 'federal' },
  { date: '2025-01-08', name: 'Новогодние каникулы', type: 'federal' },
  { date: '2025-02-23', name: 'День защитника Отечества', type: 'federal' },
  { date: '2025-03-08', name: 'Международный женский день', type: 'federal' },
  { date: '2025-05-01', name: 'Праздник Весны и Труда', type: 'federal' },
  { date: '2025-05-09', name: 'День Победы', type: 'federal' },
  { date: '2025-06-12', name: 'День России', type: 'federal' },
  { date: '2025-11-04', name: 'День народного единства', type: 'federal' },
];

/**
 * TimeIntervalCalculatorPage Component - Калькулятор промежутка времени с различными типами расчетов
 */
const TimeIntervalCalculatorPage = () => {
  const { subToolId } = useParams();
  const { darkMode, toggleDarkMode } = useTheme();

  // --- Данные инструмента ---
  const tool = useMemo(() => getToolById('date-calculator'), []);
  const pageTitle = 'Калькулятор промежутка времени | Gensio';
  const metaDescription = 'Вычисление разницы между датами в днях, неделях, месяцах, годах. Расчет рабочих дней с учетом праздников, возраста и многое другое для России и стран СНГ.';

  // --- Управление состоянием ---
  const [formData, setFormData] = useState(initialFormData);
  const [results, setResults] = useState(null);
  const [errors, setErrors] = useState({});
  const [isCalculating, setIsCalculating] = useState(false);
  const [savedCalculations, setSavedCalculations] = useState([]);
  const [holidays, setHolidays] = useState(russianHolidays);

  // --- Обновление заголовка и мета-описания ---
  useEffect(() => {
    document.title = pageTitle;
    let metaTag = document.querySelector('meta[name="description"]');
    if (!metaTag) {
      metaTag = document.createElement('meta');
      metaTag.name = 'description';
      document.head.appendChild(metaTag);
    }
    metaTag.setAttribute('content', metaDescription);
  }, [pageTitle, metaDescription]);

  // --- Загрузка сохраненных расчетов из localStorage ---
  useEffect(() => {
    const savedCalcData = localStorage.getItem('savedTimeCalculations');
    if (savedCalcData) {
      try {
        setSavedCalculations(JSON.parse(savedCalcData));
      } catch (e) {
        console.error("Error loading saved calculations:", e);
      }
    }
  }, []);

  // --- Обработка subToolId для предварительной настройки формы ---
  useEffect(() => {
    if (!subToolId) return;

    setFormData(prev => {
      let newState = { ...initialFormData };

      switch (subToolId) {
        case 'date-difference':
          newState.calculationType = 'standard';
          break;
        case 'working-days':
          newState.calculationType = 'working';
          break;
        case 'add-subtract':
          newState.calculationType = 'add-subtract';
          break;
        case 'age-calculator':
          newState.calculationType = 'age';
          newState.birthdayDate = new Date(new Date().setFullYear(new Date().getFullYear() - 30)).toISOString().split('T')[0];
          newState.referenceDate = new Date().toISOString().split('T')[0];
          break;
        default:
          newState = { ...prev };
      }
      return newState;
    });

    setResults(null);
    setErrors({});

  }, [subToolId]);

  // Сохранение расчета в localStorage
  const saveCalculation = (name) => {
    if (!results) return;

    const newSavedCalculation = {
      id: Date.now().toString(),
      name: name || `Расчет от ${new Date().toLocaleDateString('ru-RU')}`,
      date: new Date().toISOString(),
      formData: { ...formData },
      results: { ...results },
      calculationType: formData.calculationType
    };

    const updatedSavedCalculations = [...savedCalculations, newSavedCalculation];
    setSavedCalculations(updatedSavedCalculations);
    localStorage.setItem('savedTimeCalculations', JSON.stringify(updatedSavedCalculations));

    return newSavedCalculation;
  };

  // Удаление сохраненного расчета
  const deleteSavedCalculation = (id) => {
    const updatedSavedCalculations = savedCalculations.filter(calc => calc.id !== id);
    setSavedCalculations(updatedSavedCalculations);
    localStorage.setItem('savedTimeCalculations', JSON.stringify(updatedSavedCalculations));
  };

  // Загрузка сохраненного расчета
  const loadSavedCalculation = (id) => {
    const calcToLoad = savedCalculations.find(calc => calc.id === id);
    if (calcToLoad) {
      setFormData(calcToLoad.formData);
      setResults(calcToLoad.results);
    }
  };

  // --- RENDER ---
  return (
    <div className={`min-h-screen ${darkMode ? 'dark' : ''} bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-white transition-colors duration-200 font-sans`}>
      {/* Background Pattern */}
      <div aria-hidden="true" className="fixed inset-0 bg-[radial-gradient(theme(colors.gray.200)_1px,transparent_1px)] dark:bg-[radial-gradient(theme(colors.gray.800)_1px,transparent_1px)] [background-size:24px_24px] opacity-20 dark:opacity-10 pointer-events-none z-[-1]"></div>

      <Header darkMode={darkMode} toggleDarkMode={toggleDarkMode} />

      <main className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Breadcrumbs />

        <div className="flex flex-col lg:flex-row gap-8 lg:gap-12">
          {/* --- Main Content Area --- */}
          <div className="lg:w-2/3 flex-shrink-0">
            {/* Page Header */}
            <div className="mb-8">
              <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white mb-3 flex items-center gap-3">
                <CalendarDays className="w-8 h-8 text-cyan-600 dark:text-cyan-400 flex-shrink-0" />
                {tool?.name || 'Калькулятор промежутка времени'}
              </h1>
              <p className="text-lg text-gray-600 dark:text-gray-400">
                {metaDescription}
              </p>
            </div>

            {/* --- Форма калькулятора --- */}
            <CalculationForm 
              formData={formData}
              setFormData={setFormData}
              errors={errors}
              setErrors={setErrors}
              isCalculating={isCalculating}
              setIsCalculating={setIsCalculating}
              results={results}
              setResults={setResults}
              russianRegions={russianRegions}
              holidays={holidays}
              saveCalculation={saveCalculation}
              calculationType={formData.calculationType}
            />

            {/* --- Условное отображение нужных калькуляторов в зависимости от типа --- */}
            {formData.calculationType === 'working' && (
              <WorkingDaysCalculator 
                formData={formData}
                setFormData={setFormData}
                results={results}
                isCalculating={isCalculating}
                holidays={holidays}
                russianRegions={russianRegions}
              />
            )}

            {formData.calculationType === 'add-subtract' && (
              <DateAddSubtract 
                formData={formData}
                setFormData={setFormData}
                results={results}
                isCalculating={isCalculating}
                holidays={holidays}
              />
            )}

            {formData.calculationType === 'age' && (
              <AgeCalculator 
                formData={formData}
                setFormData={setFormData}
                results={results}
                isCalculating={isCalculating}
              />
            )}

            {/* --- Результаты расчета --- */}
            {results && !isCalculating && (
              <ResultsDisplay results={results} calculationType={formData.calculationType} />
            )}

            {/* --- Информационный контент о расчетах времени --- */}
            <InfoContent />
          </div>

          {/* --- Sidebar Area --- */}
          <aside className="lg:w-1/3 self-start space-y-8">
            {/* --- Панель быстрых дат --- */}
            <QuickDatesSidebar 
              formData={formData}
              setFormData={setFormData}
              setResults={setResults}
              errors={errors}
              isCalculating={isCalculating}
            />

            {/* --- Панель сохраненных расчетов --- */}
            <SavedCalculationsSidebar 
              savedCalculations={savedCalculations}
              deleteSavedCalculation={deleteSavedCalculation}
              loadSavedCalculation={loadSavedCalculation}
            />

            {/* --- Панель полезных советов --- */}
            <TipsSidebar />

            {/* --- Связанные инструменты --- */}
            {tool && (
              <ToolSidebar 
                currentToolId={tool.id} 
                currentSubToolId={subToolId} 
                tool={tool} 
              />
            )}
          </aside>
        </div>
      </main>

      <Footer />

      {/* --- Global Styles --- */}
      <style jsx global>{`
        /* Base Input Styles */
        .form-input, .form-select, .form-textarea {
          @apply block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm text-gray-900 dark:text-white transition duration-150 ease-in-out disabled:opacity-60 disabled:cursor-not-allowed dark:placeholder-gray-400;
        }
        .form-checkbox {
          @apply h-4 w-4 rounded border-gray-300 dark:border-gray-600 text-cyan-600 focus:ring-cyan-500 dark:bg-gray-700 cursor-pointer transition duration-150 ease-in-out;
        }
        .form-radio {
          @apply h-4 w-4 border-gray-300 dark:border-gray-600 text-cyan-600 focus:ring-cyan-500 dark:bg-gray-700 cursor-pointer transition duration-150 ease-in-out;
        }
        /* Error State Styling */
        .input-error {
          @apply border-red-500 dark:border-red-500 text-red-900 dark:text-red-300 placeholder-red-400 dark:placeholder-red-600 focus:border-red-500 dark:focus:border-red-500 focus:ring-red-500 dark:focus:ring-red-500;
        }

        /* Custom Calendar Styles */
        .date-range-highlight {
          @apply bg-cyan-100 dark:bg-cyan-900/40 border-cyan-300 dark:border-cyan-700;
        }
        .holiday-date {
          @apply bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 font-medium;
        }
        .weekend-date {
          @apply bg-yellow-50 dark:bg-yellow-900/10 text-yellow-700 dark:text-yellow-500;
        }
        .today-date {
          @apply ring-2 ring-cyan-500 dark:ring-cyan-400 font-bold;
        }
      `}</style>
    </div>
  );
};

export default TimeIntervalCalculatorPage;